import React, { useEffect, useState, useRef } from 'react'
import { useEnvContext } from 'next-runtime-env'
import { Button, BaseDialog, Spinner } from '@/dc-shared-web/src'
import { TokenData } from '@/integrations/trimble/types'
import { getTrimbleAuthManager } from '@/integrations/trimble/trimbleApi'
import TrimbleAuthManager from '@/integrations/trimble/trimbleAuthManager'

interface TrimbleLoginModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: (tokenData: TokenData) => void
  onError: (error: Error) => void
}

const TrimbleLoginModal: React.FC<TrimbleLoginModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  onError,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const trimbleAuthManager = useRef<TrimbleAuthManager | null>(null)
  const { NEXT_PUBLIC_PROJECTSIGHT_AUTH_URL } = useEnvContext()

  useEffect(() => {
    if (!trimbleAuthManager.current) {
      trimbleAuthManager.current = getTrimbleAuthManager(
        NEXT_PUBLIC_PROJECTSIGHT_AUTH_URL || ''
      )
    }
  }, [NEXT_PUBLIC_PROJECTSIGHT_AUTH_URL])

  useEffect(() => {
    if (isOpen && trimbleAuthManager.current) {
      handleLogin()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const handleLogin = async () => {
    if (!trimbleAuthManager.current) return

    setIsLoading(true)
    try {
      // Use the popup login method from TrimbleAuthManager
      const result = await trimbleAuthManager.current.login(true)
      if (result === true) {
        // Login successful
        const tokenData = trimbleAuthManager.current.getStoredToken()
        if (tokenData) {
          onSuccess(tokenData)
          setIsLoading(false)
          return
        }
        onError(new Error('Authentication successful but token not found'))
      }
    } catch (error) {
      console.error('Login error:', error)
      onError(
        error instanceof Error ? error : new Error('Authentication failed')
      )
    }

    // Only reach here if authentication failed or there was an error
    setIsLoading(false)
    onClose()
  }

  const handleClose = () => {
    onClose()
  }

  return (
    <BaseDialog
      title='Trimble Authentication'
      isOpen={isOpen}
      onClose={handleClose}
      size='small'
      primaryActionSlot={
        isLoading ? null : (
          <Button onClick={handleLogin} data-testid='trimble-login-btn'>
            Login with Trimble
          </Button>
        )
      }
      secondaryActionSlot={<></>}
    >
      <div className='tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-4'>
        {isLoading ? (
          <>
            <Spinner
              className='tw-h-12 tw-w-12'
              data-testid='trimble-login-spinner'
            />
            <p
              className='tw-text-neutral-500'
              data-testid='trimble-login-status'
            >
              Authenticating with Trimble...
            </p>
          </>
        ) : (
          <p className='tw-text-neutral-600 tw-text-center'>
            You&apos;ll be redirected to Trimble&apos;s authentication page in a
            popup window. Please ensure popups are allowed for this site.
          </p>
        )}
      </div>
    </BaseDialog>
  )
}

export default TrimbleLoginModal
